import React from "react";
import { Heart } from "lucide-react";
import { useWishlist } from "../context/WishlistContext";

/**
 * Heart toggle button. Filled gold when wishlisted, outline when not.
 * - size: "sm" (32px tap target) | "md" (44px) | "lg" (52px)
 * - variant: "ghost" | "solid" | "pill"
 */
export const HeartButton = ({ product, size = "md", variant = "ghost", className = "" }) => {
    const { has, toggle } = useWishlist();
    const active = has(product.slug);
    const sizes = {
        sm: { btn: "p-1.5", icon: 14 },
        md: { btn: "p-2.5", icon: 18 },
        lg: { btn: "p-3", icon: 22 },
    }[size] || { btn: "p-2.5", icon: 18 };

    const base = "inline-flex items-center justify-center rounded-full transition-all duration-200 active:scale-90 focus:outline-none focus:ring-2 focus:ring-[var(--gold)]/40";
    const variants = {
        ghost: active
            ? "bg-[rgba(233,188,11,0.15)] border border-[var(--gold)]/50 text-[var(--gold)]"
            : "bg-black/30 border border-white/10 text-white/70 hover:text-[var(--gold)] hover:border-[var(--gold)]/40",
        solid: active
            ? "bg-[var(--gold)] text-black border border-[var(--gold)]"
            : "bg-black/40 border border-white/10 text-white/80 hover:text-[var(--gold)]",
        pill: active
            ? "px-4 py-2 bg-[rgba(233,188,11,0.15)] border border-[var(--gold)]/50 text-[var(--gold)] text-xs font-mono uppercase tracking-widest gap-1.5"
            : "px-4 py-2 bg-black/30 border border-white/10 text-white/85 hover:text-[var(--gold)] hover:border-[var(--gold)]/40 text-xs font-mono uppercase tracking-widest gap-1.5",
    };

    const click = (e) => {
        e.preventDefault();
        e.stopPropagation();
        toggle(product.slug, product.name);
    };

    return (
        <button
            type="button"
            onClick={click}
            className={`${base} ${variants[variant]} ${variant !== "pill" ? sizes.btn : ""} ${className}`}
            aria-label={active ? "Remove from wishlist" : "Add to wishlist"}
            aria-pressed={active}
            data-testid={`wishlist-toggle-${product.slug}`}
        >
            <Heart
                size={sizes.icon}
                strokeWidth={2}
                fill={active ? "currentColor" : "none"}
                className={active ? "animate-[heartbeat_300ms_ease-out]" : ""}
            />
            {variant === "pill" && (
                <span>{active ? "Saved" : "Save"}</span>
            )}
            <style>{`
                @keyframes heartbeat {
                    0% { transform: scale(0.85); }
                    50% { transform: scale(1.15); }
                    100% { transform: scale(1); }
                }
            `}</style>
        </button>
    );
};
